import { useId, useState, type FormEvent } from 'react';
import { ApiError, api, type TenantBand } from './api.ts';

/**
 * The waiting-list form on the landing page.
 *
 * Its own file rather than part of `Landing.tsx` because it is the only thing
 * on that page with state, a failure mode and a promise to keep. The promise is
 * the same one the server makes: **the answer never depends on who is already
 * on the list.** Somebody submitting an address for the second time is told
 * exactly what a first-time visitor is told, because anything else would let
 * this form be used to ask whether a named person has signed up.
 *
 * Two fields are required and the rest are not, deliberately. A form that
 * demands a company name and a headcount before it will take an address
 * collects fewer addresses and better-looking data, and the addresses are the
 * point.
 */

const BANDS: readonly { readonly value: TenantBand; readonly label: string }[] = [
  { value: 'just-us', label: 'Just our own' },
  { value: '1-5', label: '1–5 clients' },
  { value: '6-25', label: '6–25 clients' },
  { value: '26-100', label: '26–100 clients' },
  { value: '100-plus', label: 'More than 100' },
];

/**
 * Deliberately vague, as the sign-in screen's is. The precise figure is not
 * useful to a person, and reading it back exactly tells an automated caller how
 * long its penalty is — which is the one audience this sentence is not for.
 */
const failureText = (cause: unknown): string => {
  if (cause instanceof ApiError) {
    if (cause.status === 429) {
      return 'Several requests have already come from this connection. If that was you, you are already on the list.';
    }
    if (cause.status === 400) {
      return 'Check the details above — one of them was not something we could store.';
    }
    return 'Something went wrong at our end. Please try again shortly.';
  }
  return 'We could not reach the server. Check your connection, then try again.';
};

type Phase = 'idle' | 'sending' | 'joined';

export const WaitlistForm = () => {
  const ids = useId();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [tenantBand, setTenantBand] = useState<TenantBand | ''>('');
  const [note, setNote] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (phase === 'sending') return;

    setPhase('sending');
    setError(null);
    try {
      await api.joinWaitlist({
        email: email.trim(),
        name: name.trim(),
        // Omitted rather than sent empty: the server stores an absent optional
        // as null, and a row full of empty strings is a row nobody can filter.
        ...(organisation.trim() === '' ? {} : { organisation: organisation.trim() }),
        ...(tenantBand === '' ? {} : { tenantBand }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      });
      setPhase('joined');
    } catch (cause) {
      setError(failureText(cause));
      setPhase('idle');
    }
  };

  if (phase === 'joined') {
    return (
      <div className="wl-done" role="status">
        <h2 className="wl-done-title">You&rsquo;re on the list.</h2>
        <p className="wl-done-body">
          We&rsquo;ll write to <strong className="wl-done-email">{email.trim()}</strong> when
          DriftWatch opens to new organisations — once, and about that. Nothing else.
        </p>
      </div>
    );
  }

  const sending = phase === 'sending';

  return (
    <form className="wl-form" onSubmit={(event) => void submit(event)} noValidate>
      <div className="wl-row">
        <div className="field">
          <label htmlFor={`${ids}-name`}>Your name</label>
          <input
            id={`${ids}-name`}
            name="name"
            type="text"
            autoComplete="name"
            required
            maxLength={120}
            disabled={sending}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor={`${ids}-email`}>Work email address</label>
          <input
            id={`${ids}-email`}
            name="email"
            type="email"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck={false}
            required
            maxLength={254}
            disabled={sending}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
      </div>

      <div className="wl-row">
        <div className="field">
          <label htmlFor={`${ids}-org`}>
            Organisation <span className="wl-optional">optional</span>
          </label>
          <input
            id={`${ids}-org`}
            name="organisation"
            type="text"
            autoComplete="organization"
            maxLength={160}
            disabled={sending}
            value={organisation}
            onChange={(event) => setOrganisation(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor={`${ids}-band`}>
            Microsoft 365 tenants you look after <span className="wl-optional">optional</span>
          </label>
          <select
            id={`${ids}-band`}
            name="tenantBand"
            disabled={sending}
            value={tenantBand}
            onChange={(event) => setTenantBand(event.target.value as TenantBand | '')}
          >
            <option value="">Prefer not to say</option>
            {BANDS.map((band) => (
              <option key={band.value} value={band.value}>
                {band.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label htmlFor={`${ids}-note`}>
          Which framework is on your desk right now? <span className="wl-optional">optional</span>
        </label>
        <textarea
          id={`${ids}-note`}
          name="note"
          rows={3}
          maxLength={1000}
          disabled={sending}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <p className="wl-hint">
          It decides what we build next, so it is the most useful thing on this form.
        </p>
      </div>

      {/*
        `assertive`, as the sign-in screen's is: this replaces what the person
        was about to do rather than adding to it.
      */}
      <p aria-live="assertive" role="alert" className="wl-error-live">
        {error !== null && <span className="error">{error}</span>}
      </p>

      <div className="wl-actions">
        <button type="submit" className="btn btn-primary" aria-busy={sending || undefined} disabled={sending}>
          {sending ? 'Adding you…' : 'Join the waiting list'}
        </button>
        <p className="wl-privacy">
          One email when we open. No newsletter, and your details are never passed on. We keep the
          IP address you sign up from, only to stop automated sign-ups.
        </p>
      </div>
    </form>
  );
};
